import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, ChevronLeft, ChevronRight, Trash2, Upload, X } from "lucide-react";
import { Button } from "@telluscompany/design-system";

import { useLocalization } from "@i18n";
import { buildAssetUrl } from "@shared/utils";

import {
  useSaveInspectionMutation,
  useUploadInspectionPhotoMutation,
  type InspectionAttachment,
  type InspectionDetail,
  type InspectionStatus,
} from "../../../api/inspection";
import { INSPECTION_PHOTOS_LABEL_KEY } from "../inspection-form.catalog";

/** Endpoints §11 — evidência aceita apenas imagem (sem vídeo na v1). */
export const INSPECTION_PHOTO_ACCEPT = "image/jpeg,image/jpg,image/png,image/webp";
const ALLOWED_MIME_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
export const INSPECTION_PHOTO_MAX_BYTES = 10 * 1024 * 1024;

const EDITABLE_STATUSES: InspectionStatus[] = ["open", "inProgress"];
const PLACEHOLDER_THUMB_COUNT = 2;

type PendingPhoto = {
  file: File;
  objectUrl: string;
};

export interface InspectionPhotoEvidenceProps {
  structureId: string;
  inspectionId: string;
  itemKey: string;
  occurrenceKey?: string;
  canWrite: boolean;
  inspectionStatus: InspectionStatus;
  admitsEvidence: boolean;
  attachments?: InspectionAttachment[];
  /** Item em anomalia — exige pelo menos 1 foto (form §5.1). */
  anomalyActive?: boolean;
  invalid?: boolean;
  disabled?: boolean;
  onCountChange?: (count: number) => void;
  /** PATCH Y=6 de remove devolve o detalhe — a ficha precisa sincronizar anexos sem avançar o step. */
  onDetailSync?: (detail: InspectionDetail) => void;
}

const InspectionPhotoEvidence = ({
  structureId,
  inspectionId,
  itemKey,
  occurrenceKey = "",
  canWrite,
  inspectionStatus,
  admitsEvidence,
  attachments = [],
  anomalyActive = false,
  invalid = false,
  disabled = false,
  onCountChange,
  onDetailSync,
}: InspectionPhotoEvidenceProps) => {
  const { translate } = useLocalization();
  const [uploaded, setUploaded] = useState<InspectionAttachment[]>([]);
  const [removedIds, setRemovedIds] = useState<string[]>([]);
  /** Uploads em voo contam como evidência; pendente local / falho / removida não. */
  const [uploadInFlight, setUploadInFlight] = useState(0);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [pendingPhoto, setPendingPhoto] = useState<PendingPhoto | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingRemove, setPendingRemove] =
    useState<InspectionAttachment | null>(null);
  const [overlayHost, setOverlayHost] = useState<HTMLElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { mutate: uploadPhoto } = useUploadInspectionPhotoMutation(
    structureId,
    inspectionId,
  );
  const { mutate: saveInspection, isPending: isRemoving } =
    useSaveInspectionMutation(structureId, inspectionId);

  const inputId = `inspection-photo-${itemKey}-${occurrenceKey || "root"}`;

  const visibleAttachments = useMemo(() => {
    const merged = [...attachments, ...uploaded];
    const seen = new Set<string>();

    return merged.filter((attachment) => {
      if (removedIds.includes(attachment.id) || seen.has(attachment.id)) {
        return false;
      }
      seen.add(attachment.id);
      return true;
    });
  }, [attachments, uploaded, removedIds]);

  const photoCount = visibleAttachments.length + uploadInFlight;

  useEffect(() => {
    onCountChange?.(photoCount);
  }, [photoCount, onCountChange]);

  useEffect(() => {
    if (!pendingPhoto) return undefined;
    const objectUrl = pendingPhoto.objectUrl;
    return () => URL.revokeObjectURL(objectUrl);
  }, [pendingPhoto]);

  /**
   * O carrossel do `ImageUpload` (`enableCarouselPreview`) não é exportado e
   * o widget inteiro sobe arquivo no select — incompatível com §5.2. O overlay
   * replica o chrome desse carrossel (fundo preto, setas, N de M) no host da
   * ficha: o Dialog põe `pointer-events: none` no `body`, então o portal tem
   * de ser `[data-slot="dialog-content"]` (`pointer-events: auto`).
   */
  useLayoutEffect(() => {
    if (previewIndex === null && !pendingPhoto && !pendingRemove) {
      setOverlayHost(null);
      return;
    }

    setOverlayHost(
      rootRef.current?.closest<HTMLElement>("[data-slot='dialog-content']") ??
        document.body,
    );
  }, [previewIndex, pendingPhoto, pendingRemove]);

  const closePreview = () => {
    if (isSaving) return;
    setPendingPhoto(null);
    setPreviewIndex(null);
  };

  useEffect(() => {
    if (previewIndex === null && !pendingPhoto && !pendingRemove) {
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        if (pendingPhoto || isSaving || previewIndex === null) return;
        if (visibleAttachments.length < 2) return;
        event.preventDefault();
        event.stopPropagation();
        const delta = event.key === "ArrowLeft" ? -1 : 1;
        const next = previewIndex + delta;
        if (next < 0 || next >= visibleAttachments.length) return;
        setPreviewIndex(next);
        return;
      }

      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      if (isSaving) return;
      if (previewIndex !== null || pendingPhoto) {
        setPendingPhoto(null);
        setPreviewIndex(null);
        return;
      }
      setPendingRemove(null);
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [
    previewIndex,
    pendingPhoto,
    pendingRemove,
    isSaving,
    visibleAttachments.length,
  ]);

  const canUpload =
    canWrite && admitsEvidence && EDITABLE_STATUSES.includes(inspectionStatus);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_MIME_TYPES.includes(file.type.toLowerCase())) {
      setValidationError(
        `${translate("errors.error15")} - COD. 6.10.15`,
      );
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    if (file.size > INSPECTION_PHOTO_MAX_BYTES) {
      setValidationError(
        `${translate("inspection.photos.tooLarge")} - COD. 6.10.16`,
      );
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    setValidationError(null);
    setPreviewIndex(null);
    setPendingPhoto({
      file,
      objectUrl: URL.createObjectURL(file),
    });

    if (inputRef.current) inputRef.current.value = "";
  };

  const handleSavePending = () => {
    if (!pendingPhoto || isSaving) return;

    const { file } = pendingPhoto;
    setIsSaving(true);
    setUploadInFlight((current) => current + 1);
    uploadPhoto(
      { itemKey, occurrenceKey, file },
      {
        onSuccess: (attachment) => {
          setUploaded((current) => [...current, attachment]);
          setPendingPhoto(null);
          setUploadInFlight((current) => Math.max(0, current - 1));
          setIsSaving(false);
        },
        onError: () => {
          setUploadInFlight((current) => Math.max(0, current - 1));
          setIsSaving(false);
        },
      },
    );
  };

  const handleConfirmRemove = () => {
    const attachmentId = pendingRemove?.id;
    if (!attachmentId) return;

    saveInspection(
      { removeAttachmentIds: [attachmentId] },
      {
        onSuccess: (saved) => {
          setRemovedIds((current) => [...current, attachmentId]);
          setPendingRemove(null);
          setPreviewIndex(null);
          onDetailSync?.(saved);
        },
      },
    );
  };

  if (!admitsEvidence) return null;

  const thumbCount = Math.max(PLACEHOLDER_THUMB_COUNT, visibleAttachments.length);
  const photosLabel = (
    <>
      {translate(INSPECTION_PHOTOS_LABEL_KEY)}
      {anomalyActive ? (
        <>
          {" "}
          <span className="insp-req" aria-hidden>
            *
          </span>
        </>
      ) : null}
    </>
  );

  const lightboxOpen = Boolean(pendingPhoto) || previewIndex !== null;
  const preview =
    previewIndex !== null ? (visibleAttachments[previewIndex] ?? null) : null;
  const previewName =
    pendingPhoto?.file.name ?? preview?.originalFileName ?? "";
  const previewSrc = pendingPhoto
    ? pendingPhoto.objectUrl
    : preview?.storagePath
      ? buildAssetUrl(preview.storagePath)
      : "";
  const canCycleSaved =
    !pendingPhoto && !isSaving && visibleAttachments.length > 1;
  const showPrev = canCycleSaved && (previewIndex ?? 0) > 0;
  const showNext =
    canCycleSaved &&
    previewIndex !== null &&
    previewIndex < visibleAttachments.length - 1;

  return (
    <div ref={rootRef} className="mt-2 flex flex-col gap-1">
      <div className="insp-foto-head">
        {canUpload ? (
          <label htmlFor={inputId} className="insp-foto-lab">
            {photosLabel}
          </label>
        ) : (
          <span className="insp-foto-lab">{photosLabel}</span>
        )}
        {canUpload ? (
          <button
            type="button"
            className="insp-foto-upload"
            aria-hidden
            tabIndex={-1}
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
          >
            <Upload aria-hidden size={16} />
          </button>
        ) : null}
      </div>
      {canUpload ? (
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={INSPECTION_PHOTO_ACCEPT}
          required={anomalyActive}
          aria-invalid={invalid ? "true" : undefined}
          disabled={disabled}
          onChange={handleChange}
          className="sr-only"
        />
      ) : null}

      {canUpload ? (
        <p className="insp-hint">{translate("inspection.photos.acceptHint")}</p>
      ) : null}

      {invalid ? (
        <p role="alert" className="insp-field-error">
          {translate("inspection.form.fieldErrorPhotos")}
        </p>
      ) : null}

      {validationError ? (
        <p role="alert" className="insp-field-error">
          {validationError}
        </p>
      ) : null}

      <ul className="insp-foto-row">
        {Array.from({ length: thumbCount }, (_, index) => {
          const attachment = visibleAttachments[index];

          return (
            <li
              key={attachment?.id ?? `insp-foto-placeholder-${index}`}
              className="insp-foto-thumb"
            >
              {attachment?.storagePath ? (
                <button
                  type="button"
                  className="insp-foto-thumb-open"
                  onClick={() => {
                    setPendingPhoto(null);
                    setPreviewIndex(index);
                  }}
                  aria-label={translate("inspection.photos.expand", {
                    name: attachment.originalFileName,
                  })}
                >
                  <img
                    src={buildAssetUrl(attachment.storagePath)}
                    alt=""
                  />
                </button>
              ) : null}
              {attachment ? (
                <span className="sr-only">{attachment.originalFileName}</span>
              ) : null}
              {attachment && canUpload && !disabled ? (
                <button
                  type="button"
                  className="insp-foto-thumb-remove"
                  aria-label={translate("inspection.photos.remove")}
                  onClick={() => {
                    setPreviewIndex(null);
                    setPendingRemove(attachment);
                  }}
                >
                  <X aria-hidden size={12} />
                </button>
              ) : null}
            </li>
          );
        })}
      </ul>

      {overlayHost && lightboxOpen && previewSrc
        ? createPortal(
            <div
              className={
                isSaving
                  ? "insp-foto-lightbox is-saving"
                  : "insp-foto-lightbox"
              }
              role="dialog"
              aria-modal="true"
              aria-busy={isSaving || undefined}
              aria-label={translate("inspection.photos.preview", {
                name: previewName,
              })}
              data-testid="inspection-photo-lightbox"
              onClick={closePreview}
            >
              <div
                className="insp-foto-lightbox-toolbar"
                onClick={(event) => event.stopPropagation()}
              >
                {pendingPhoto ? (
                  <Button
                    type="button"
                    variant="primary"
                    disabled={isSaving}
                    onClick={handleSavePending}
                  >
                    {translate("inspection.photos.save")}
                  </Button>
                ) : null}
                <button
                  type="button"
                  className="insp-foto-lightbox-close"
                  aria-label={translate("inspection.photos.closePreview")}
                  disabled={isSaving}
                  onClick={closePreview}
                >
                  <X aria-hidden size={16} />
                </button>
              </div>
              {showPrev ? (
                <button
                  type="button"
                  className="insp-foto-lightbox-nav insp-foto-lightbox-nav-prev"
                  aria-label={translate("inspection.photos.previous")}
                  onClick={(event) => {
                    event.stopPropagation();
                    setPreviewIndex((current) =>
                      current === null ? current : current - 1,
                    );
                  }}
                >
                  <ChevronLeft aria-hidden size={28} />
                </button>
              ) : null}
              {showNext ? (
                <button
                  type="button"
                  className="insp-foto-lightbox-nav insp-foto-lightbox-nav-next"
                  aria-label={translate("inspection.photos.next")}
                  onClick={(event) => {
                    event.stopPropagation();
                    setPreviewIndex((current) =>
                      current === null ? current : current + 1,
                    );
                  }}
                >
                  <ChevronRight aria-hidden size={28} />
                </button>
              ) : null}
              <div
                className="insp-foto-lightbox-frame"
                onClick={(event) => event.stopPropagation()}
              >
                <img src={previewSrc} alt={previewName} />
              </div>
              {!pendingPhoto && visibleAttachments.length > 0 ? (
                <div
                  className="insp-foto-lightbox-counter"
                  data-testid="inspection-photo-lightbox-counter"
                  onClick={(event) => event.stopPropagation()}
                >
                  {translate("inspection.photos.counter", {
                    current: (previewIndex ?? 0) + 1,
                    total: visibleAttachments.length,
                  })}
                </div>
              ) : null}
            </div>,
            overlayHost,
          )
        : null}

      {overlayHost && pendingRemove
        ? createPortal(
            <div
              className="insp-foto-confirm"
              role="dialog"
              aria-modal="true"
              aria-label={translate("inspection.photos.deleteTitle")}
              data-testid="inspection-photo-delete-confirm"
              onClick={() => {
                if (!isRemoving) setPendingRemove(null);
              }}
            >
              <div
                className="insp-foto-confirm-card"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="insp-foto-confirm-head">
                  <span className="insp-foto-confirm-icon" aria-hidden>
                    <Trash2 size={22} />
                  </span>
                  <p className="insp-foto-confirm-title">
                    {translate("inspection.photos.deleteTitle")}
                  </p>
                </div>
                <div className="insp-foto-confirm-item">
                  <p className="insp-foto-confirm-caption">
                    {translate("inspection.photos.deleteCaption")}
                  </p>
                  <p className="insp-foto-confirm-name">
                    {pendingRemove.originalFileName}
                  </p>
                </div>
                <div className="insp-foto-confirm-alert">
                  <AlertTriangle size={18} aria-hidden />
                  <p>{translate("inspection.photos.deleteDescription")}</p>
                </div>
                <div className="insp-foto-confirm-actions">
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={isRemoving}
                    onClick={() => setPendingRemove(null)}
                  >
                    {translate("inspection.photos.deleteCancel")}
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    disabled={isRemoving}
                    onClick={handleConfirmRemove}
                  >
                    {translate("inspection.photos.deleteConfirm")}
                  </Button>
                </div>
              </div>
            </div>,
            overlayHost,
          )
        : null}
    </div>
  );
};

export default InspectionPhotoEvidence;
